// Analytics on a landing: the Meta Pixel and PostHog.
//
// The pixel's base code is inline in the head of src/template.html, where
// Meta wants it; PageView and ViewContent fire there on every load. This
// module owns the click events and the PostHog setup.
//
// Nothing here is required for the page to work. Both tags are off on
// localhost, blocked outright by plenty of browsers and extensions, and must
// never be able to break the page — so every call is guarded.

import type { PostHog } from 'posthog-js'

/**
 * Meta's standard event for reaching out through a direct channel — here, a
 * click on a WhatsApp link. A click is all the browser can see: the chat
 * itself happens in WhatsApp, so this is intent, not a conversation.
 */
export const CONTACT_EVENT = 'Contact'

/**
 * A click on a free-trial link, which leaves for the app's pricing page and
 * Stripe Checkout. Meta's 'InitiateCheckout', not 'StartTrial': the trial only
 * starts once Checkout completes, on another origin this pixel never sees.
 */
export const TRIAL_CLICK_EVENT = 'InitiateCheckout'

type Fbq = (command: string, event: string, params?: Record<string, unknown>, options?: { eventID?: string }) => void
type PixelScope = { fbq?: Fbq }

/**
 * Hosts analytics report from. Local development is excluded so day-to-day
 * work never shows up as traffic — the same check runs in the inline pixel
 * code, so nothing is even downloaded on localhost.
 */
export function isTrackedHost(hostname: string): boolean {
  return (
    hostname !== '' &&
    hostname !== 'localhost' &&
    hostname !== '127.0.0.1' &&
    hostname !== '[::1]' &&
    !hostname.endsWith('.local') &&
    !hostname.endsWith('.localhost')
  )
}

// ── PostHog ─────────────────────────────────────────────────────────────────

export type PosthogSetup = {
  key: string
  host: string
  product: string
  locale: string
  debug?: boolean
}

/**
 * What a landing hands to posthog.init. Cookieless on purpose
 * (`persistence: 'memory'`): a landing has nobody to recognise, and no cookie
 * means no consent banner question on top of the one already open for the
 * pixel. Session replay works without it. Inputs are masked; the copy is
 * public and stays readable in replays.
 */
export function posthogOptions(setup: PosthogSetup) {
  return {
    api_host: setup.host,
    persistence: 'memory' as const,
    person_profiles: 'identified_only' as const,
    capture_pageview: true,
    autocapture: true,
    session_recording: { maskAllInputs: true },
    debug: setup.debug ?? false,
  }
}

/** Properties every event and replay from this page carries. One PostHog project serves every product. */
export function posthogRegister(setup: PosthogSetup) {
  return { product: setup.product, surface: 'landing' as const, locale: setup.locale }
}

// Loaded on demand: posthog-js is ~100 KB gzipped, so the page's own script
// stays small and a build without a key never downloads it.
let posthog: PostHog | null = null

export function initPosthog(setup: PosthogSetup): void {
  import('posthog-js')
    .then(({ default: client }) => {
      client.init(setup.key, posthogOptions(setup))
      client.register(posthogRegister(setup))
      posthog = client
    })
    .catch(() => {
      // An analytics failure must never be a page failure.
    })
}

function capture(event: string, properties?: Record<string, string>): void {
  if (!posthog) return
  try {
    posthog.capture(event, properties)
  } catch {
    // Same.
  }
}

// ── Events ──────────────────────────────────────────────────────────────────

/** Fired on a click on any `[data-contact]` link. */
export function trackContact(scope: PixelScope = globalThis as PixelScope): void {
  scope.fbq?.('track', CONTACT_EVENT)
  capture('contact_click')
}

/** Fired on a click on any `[data-trial]` link. */
/** A store badge on an app-kind landing: which store, never who. No pixel event — the health apps carry none. */
export function trackStoreClick(store: string): void {
  capture('store_click', { store })
}

/** The press form was submitted (and the mail client opened). Counted, never read. */
export function trackPressInquiry(): void {
  capture('press_inquiry')
}

export function trackTrialClick(scope: PixelScope = globalThis as PixelScope): void {
  scope.fbq?.('track', TRIAL_CLICK_EVENT)
  capture('trial_click')
}
